<template>
    <v-card elevation="0" dark color="transparent">
        <div class="register-serial-shell">
            <div class="register-serial-hero">
                <div class="register-kicker">
                    <v-icon small class="register-kicker-icon">{{ mdiCheckboxMarkedCircleOutline }}</v-icon>
                    <span>SERIAL INPUT</span>
                </div>
                <h1 class="register-title">Register Serial Number</h1>
                <p class="register-description">
                    Please enter the serial number you would like to register, add any asset tag or location details, and then
                    press 'register' to complete.
                </p>
                <div class="register-hero-accent"></div>
            </div>

            <v-row class="register-serial-grid" align="stretch">
                <v-col cols="12" lg="12" class="register-form-panel">
                    <v-card class="register-form-card" elevation="0" dark>
                        <v-row dense>
                            <v-col v-if="userIsOrgAdmin" cols="12" md="6">
                                <v-select
                                    v-model="registerSerialToOrg"
                                    dark
                                    outlined
                                    dense
                                    hide-details
                                    return-object
                                    item-text="organizationName"
                                    label="Select Organization"
                                    clearable
                                    :items="orgList"
                                    :loading="loading"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon class="mt-1 mr-1" small>
                                            {{ mdiAccountSupervisorCircle }}
                                        </v-icon>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="serialNumber"
                                    outlined
                                    dark
                                    label="Serial Number"
                                    dense
                                    hide-details="auto"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon class="mt-1 mr-1" small>
                                            {{ mdiBarcode }}
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="assetTag"
                                    outlined
                                    dark
                                    label="Asset Tag"
                                    dense
                                    hide-details="auto"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon class="mt-1 mr-1" small>
                                            {{ mdiTag }}
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="location"
                                    outlined
                                    dark
                                    label="Location"
                                    dense
                                    hide-details="auto"
                                    prepend-inner-icon="mdi-map-marker-outline"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon class="mt-1 mr-1" small>
                                            {{ mdiMapMarker }}
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <div class="register-action-divider"></div>
                        <div class="register-actions">
                            <v-btn
                                class="register-submit-btn"
                                color="primary"
                                outlined
                                dark
                                large
                                :loading="serialSubmitLoading"
                                :disabled="serialSubmitDisabled"
                                @click="
                                    $emit('submit-serial', { serialNumber, assetTag, location, registerSerialToOrg })
                                "
                            >
                                <span class="register-submit-content">
                                    <v-icon small left>{{ mdiArrowRightBoldCircle }}</v-icon>
                                    Register Serial
                                </span>
                            </v-btn>
                        </div>

                        <div class="register-result">
                            <div class="register-result-label">
                                <v-icon small class="register-result-icon">
                                    {{ mdiHistory }}
                                </v-icon>
                                <span>Submitted serial(s)</span>
                            </div>
                            <transition name="register-result-fade">
                                <div v-if="successfulRegistrations.length" class="register-result-table">
                                    <v-data-table
                                        style="background-color: transparent"
                                        dark
                                        disable-sort
                                        no-data-text="No serial(s) submitted"
                                        :headers="successHeaders"
                                        :items="successfulRegistrations"
                                    >
                                        <template v-slot:item.iw="{ item }">
                                            {{ item.iw ? 'Yes' : 'No' }}
                                        </template>
                                    </v-data-table>
                                </div>
                            </transition>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </v-card>
</template>

<script>
import {
    mdiCheckboxMarkedCircleOutline,
    mdiAccountSupervisorCircle,
    mdiBarcode,
    mdiTag,
    mdiMapMarker,
    mdiArrowRightBoldCircle,
    mdiHistory
} from '@mdi/js'

export default {
    name: 'RegisterSerialTab',
    props: {
        userIsOrgAdmin: {
            type: Boolean,
            required: true
        },
        orgList: {
            type: Array,
            default: () => []
        },
        serialSubmitLoading: {
            type: Boolean,
            default: false
        },
        successfulRegistrations: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            mdiCheckboxMarkedCircleOutline,
            mdiAccountSupervisorCircle,
            mdiBarcode,
            mdiTag,
            mdiMapMarker,
            mdiArrowRightBoldCircle,
            mdiHistory,
            registerSerialToOrg: '',
            serialNumber: '',
            assetTag: '',
            location: '',
            successHeaders: [
                {
                    text: 'Serial #',
                    value: 'serial',
                    align: 'left'
                },
                {
                    text: 'Model',
                    value: 'model',
                    align: 'left'
                },
                {
                    text: 'Organization',
                    value: 'org',
                    align: 'left'
                },
                {
                    text: 'Asset Tag',
                    value: 'assetTag',
                    align: 'left'
                },
                {
                    text: 'Location',
                    value: 'location',
                    align: 'left'
                },
                {
                    text: 'In Warranty',
                    value: 'iw',
                    align: 'left'
                }
            ]
        }
    },
    computed: {
        serialSubmitDisabled() {
            if (this.serialSubmitLoading) {
                return true
            }

            if (!this.serialNumber || !this.serialNumber.trim()) {
                return true
            }

            if (this.userIsOrgAdmin) {
                return !this.registerSerialToOrg || !this.registerSerialToOrg.orgId
            }

            return false
        }
    }
}
</script>

<style scoped>
.register-serial-tab {
    padding-top: 18px;
}

.register-serial-shell {
    position: relative;
    overflow: hidden;
    padding: 8px 0 6px;
}

.register-serial-shell::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(219, 81, 32, 0.22), transparent 34%),
        radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.08), transparent 30%);
    pointer-events: none;
}

.register-serial-hero {
    position: relative;
    z-index: 1;
    margin-bottom: 24px;
    max-width: 860px;
}

.register-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 6px 12px;
    border: 1px solid rgba(219, 81, 32, 0.3);
    border-radius: 999px;
    background: rgba(219, 81, 32, 0.12);
    color: #db5120;
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.register-kicker-icon {
    color: #db5120 !important;
}

.register-title {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3.25rem);
    line-height: 1.02;
    letter-spacing: -0.08rem;
}

.register-description {
    margin: 14px 0 0;
    max-width: 62ch;
    font-size: 1rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.76);
}

.register-hero-accent {
    margin-top: 14px;
    width: min(420px, 92%);
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, #db5120 0%, rgba(219, 81, 32, 0.22) 100%);
}
.register-serial-grid {
    position: relative;
    z-index: 1;
}

.register-form-panel {
    display: flex;
}

.register-form-card {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 20px !important;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03)) !important;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.register-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
}

.register-submit-btn {
    min-width: 180px;
    letter-spacing: 0.04em;
}

.register-submit-content {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.register-divider {
    margin: 20px 0 !important;
    border-color: rgba(255, 255, 255, 0.14) !important;
}

.register-result {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.register-result-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.register-result-icon {
    color: #db5120 !important;
}

.register-result-table {
    padding-top: 4px;
}

.register-result-fade-enter-active,
.register-result-fade-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}

.register-result-fade-enter,
.register-result-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

@media (max-width: 959px) {
    .register-form-card {
        padding: 20px;
    }

    .register-actions {
        justify-content: flex-start;
    }

    .register-submit-btn {
        width: 100%;
        min-width: 0;
    }
}

@media (max-width: 600px) {
    .register-serial-tab {
        padding-top: 8px;
    }

    .register-serial-shell::before {
        display: none;
    }

    .register-serial-shell {
        padding-top: 0;
    }

    .register-title {
        font-size: clamp(1.8rem, 8vw, 2.4rem);
    }

    .register-description {
        font-size: 0.95rem;
    }
}
</style>
